import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import {
  CompleteSubTaskRequest,
  CreateSubTaskRequest,
  InstanceComment,
  WfSubTask
} from './wf-runtime-types';

/** Minimal directory result shape for the assignee/escalation picker. */
export interface DirectoryItem { id: string; displayName?: string; email?: string; }

/**
 * H.6-a — Sub-task API client. Mirrors WfTaskApi's auth/header pattern
 * so it slots into the same DI scope.
 */
@Injectable({ providedIn: 'root' })
export class WfSubTaskApi {
  private readonly base: string;
  private readonly apiRoot: string;

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    appConfig: AppConfigService
  ) {
    this.apiRoot = appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.base = `${this.apiRoot}/wf`;
  }

  /**
   * R2.3 — user/group directory search for the consult assignee/escalation
   * picker. Uses HttpClient + the injected AuthenticationService token, so it
   * works everywhere (incl. the standalone My Work modal, where the Form.io
   * renderer's window.__skyplusGetToken global is not published).
   */
  searchDirectory(kind: 'USER' | 'GROUP', term: string): Observable<DirectoryItem[]> {
    const path = kind === 'GROUP' ? 'groups' : 'users';
    const url = `${this.apiRoot}/mail/workflow/${path}/search`
              + `?filter=${encodeURIComponent(term)}&maxItems=20`;
    return this.http.get<any>(url, { headers: this.authHeaders() }).pipe(
      map(json => (kind === 'GROUP' ? json?.groups : json?.users) ?? [])
    );
  }

  /** POST /api/wf/instances/{instanceId}/subtasks */
  create(instanceId: number, req: CreateSubTaskRequest): Observable<WfSubTask> {
    return this.http.post<WfSubTask>(
      `${this.base}/instances/${instanceId}/subtasks`,
      req,
      { headers: this.jsonHeaders() }
    );
  }

  /** GET /api/wf/instances/{instanceId}/subtasks */
  listByInstance(instanceId: number): Observable<WfSubTask[]> {
    return this.http.get<WfSubTask[]>(
      `${this.base}/instances/${instanceId}/subtasks`,
      { headers: this.authHeaders() }
    );
  }

  /** GET /api/wf/subtasks/my */
  listMine(): Observable<WfSubTask[]> {
    return this.http.get<WfSubTask[]>(
      `${this.base}/subtasks/my`,
      { headers: this.authHeaders() }
    );
  }

  /** PATCH /api/wf/subtasks/{id}/complete */
  complete(id: number, req?: CompleteSubTaskRequest): Observable<WfSubTask> {
    return this.http.patch<WfSubTask>(
      `${this.base}/subtasks/${id}/complete`,
      req ?? {},
      { headers: this.jsonHeaders() }
    );
  }

  /** PATCH /api/wf/subtasks/{id}/cancel */
  cancel(id: number): Observable<WfSubTask> {
    return this.http.patch<WfSubTask>(
      `${this.base}/subtasks/${id}/cancel`,
      {},
      { headers: this.jsonHeaders() }
    );
  }

  /** R2.3 — GET /api/wf/subtasks/{id}/comments (consultation thread). */
  listComments(id: number): Observable<InstanceComment[]> {
    return this.http.get<InstanceComment[]>(
      `${this.base}/subtasks/${id}/comments`,
      { headers: this.authHeaders() }
    );
  }

  /** R2.3 — POST /api/wf/subtasks/{id}/comments (add/reply within a consultation). */
  addComment(id: number, body: string): Observable<InstanceComment> {
    return this.http.post<InstanceComment>(
      `${this.base}/subtasks/${id}/comments`,
      { body },
      { headers: this.jsonHeaders() }
    );
  }

  // ─── Phase 3 — distribution ──────────────────────────────────────────────

  /** Keycloak role search for distribution GROUP recipients (matches claimable/membership). */
  searchRoles(term: string): Observable<DirectoryItem[]> {
    const url = `${this.apiRoot}/mail/workflow/roles/search`
              + `?filter=${encodeURIComponent(term)}&maxItems=20`;
    return this.http.get<any>(url, { headers: this.authHeaders() }).pipe(
      map(json => (json?.roles ?? []).map((r: any) => ({ id: r.id ?? r.name, displayName: r.name ?? r.id })))
    );
  }

  /** POST /api/wf/instances/{id}/distribute */
  distribute(instanceId: number, req: any): Observable<WfSubTask[]> {
    return this.http.post<WfSubTask[]>(
      `${this.base}/instances/${instanceId}/distribute`, req, { headers: this.jsonHeaders() });
  }

  /** GET /api/wf/instances/{id}/distributions — batches with returned/outstanding. */
  getDistributions(instanceId: number): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.base}/instances/${instanceId}/distributions`, { headers: this.authHeaders() });
  }

  /** POST /api/wf/instances/{id}/distributions/force-close — owner force-proceed. */
  forceCloseBlocking(instanceId: number): Observable<{ closed: number }> {
    return this.http.post<{ closed: number }>(
      `${this.base}/instances/${instanceId}/distributions/force-close`, {}, { headers: this.jsonHeaders() });
  }

  private authHeaders(): HttpHeaders {
    const token = this.auth.getToken();
    return token
      ? new HttpHeaders({ 'Authorization': `Bearer ${token}` })
      : new HttpHeaders();
  }

  private jsonHeaders(): HttpHeaders {
    return this.authHeaders().set('Content-Type', 'application/json');
  }
}